import { Tabs as BaseTabs } from '@base-ui/react/tabs';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

/** Return the classes of a tab trigger, for tab strips built from the same primitives. */
export const tabTriggerClassName = ({ isActive, isDisabled }: { isActive: boolean; isDisabled: boolean }) =>
  twMerge(
    'inline-flex items-center whitespace-nowrap border-b-2 border-transparent bg-transparent px-2 py-1.5',
    'text-sm font-medium leading-5 text-content-subtlest transition-colors duration-200',
    isActive && 'border-border-active text-content-subtle',
    isDisabled ? 'cursor-default' : 'cursor-pointer hover:text-slate-600',
    'focus:outline-none focus:ring-0'
  );

type Tab = {
  label: string;
  labelContent?: React.ReactNode;
  content?: React.ReactNode;
};

type TabsProps = {
  tabs: Tab[];
  disable?: boolean;
  onTabChange?: (index: number, label: string) => void;
  activeIndexExternal?: number;
  fullHeight?: boolean;
  className?: string;
  /** Extra classes on the tab list (e.g. horizontal page padding). */
  listClassName?: string;
  contentMargin?: boolean;
};

const Tabs: React.FC<TabsProps> = ({
  tabs,
  disable = false,
  onTabChange,
  activeIndexExternal,
  fullHeight = false,
  className = '',
  listClassName = '',
  contentMargin = true
}) => {
  const [uncontrolledIndex, setUncontrolledIndex] = useState(activeIndexExternal ?? 0);
  const isControlled = typeof activeIndexExternal === 'number';
  const activeIndex = isControlled ? activeIndexExternal : uncontrolledIndex;

  const selectTab = (index: number) => {
    if (!isControlled) setUncontrolledIndex(index);
    onTabChange?.(index, tabs[index]?.label);
  };

  return (
    <BaseTabs.Root
      value={activeIndex}
      className={twMerge(fullHeight && 'flex h-full flex-col gap-4', className)}
      onValueChange={(value) => {
        if (disable) return;
        const index = typeof value === 'number' ? value : Number(value);
        if (!Number.isNaN(index)) selectTab(index);
      }}
    >
      <div className="relative w-full">
        <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-0.5 bg-border-default" />
        <div className="relative z-10 w-full overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <BaseTabs.List className={twMerge('flex min-w-max items-end gap-2.5', listClassName)}>
            {tabs.map((tab, index) => (
              <BaseTabs.Tab
                key={index}
                value={index}
                disabled={disable}
                className={twMerge(
                  'relative inline-flex items-center whitespace-nowrap bg-transparent px-2 py-1.5',
                  'text-sm font-medium leading-5 text-content-subtlest',
                  index === activeIndex &&
                    "text-content-active font-semibold after:absolute after:inset-x-0 after:bottom-0 after:z-10 after:h-0.5 after:bg-border-active after:content-['']",
                  disable ? 'cursor-default' : 'cursor-pointer',
                  'focus:outline-none focus:ring-0'
                )}
              >
                {tab.labelContent ?? tab.label}
              </BaseTabs.Tab>
            ))}
          </BaseTabs.List>
        </div>
      </div>

      {tabs.map(
        (tab, index) =>
          tab.content && (
            <BaseTabs.Panel key={index} value={index} className={`min-h-0 flex-1 ${contentMargin ? 'mt-4' : ''}`}>
              {tab.content}
            </BaseTabs.Panel>
          )
      )}
    </BaseTabs.Root>
  );
};

export default Tabs;
